import {
  createWindDesign,
  updateWindDesign,
  windDesignNames,
} from "./wind-designs.js";
import {
  createNumericDesign,
  updateNumericDesign,
  numericDesignNames,
} from "./numeric-designs.js";

import {
  createSimpleWindDesign,
  updateSimpleWindDesign,
  simpleWindDesignNames,
} from "./simple-wind-designs.js";
import {
  createSimpleNumericDesign,
  updateSimpleNumericDesign,
  simpleNumericDesignNames,
} from "./simple-numeric-designs.js";

export const instrumentDesignNames = {
  wind: { ...windDesignNames, ...simpleWindDesignNames },
  number: { ...numericDesignNames, ...simpleNumericDesignNames },
};
function designRenderer(kind, variant) {
  if (kind === "wind")
    return Object.hasOwn(simpleWindDesignNames, variant)
      ? [createSimpleWindDesign, updateSimpleWindDesign]
      : [createWindDesign, updateWindDesign];
  return Object.hasOwn(simpleNumericDesignNames, variant)
    ? [createSimpleNumericDesign, updateSimpleNumericDesign]
    : [createNumericDesign, updateNumericDesign];
}
const channels = {
  wind: ["heading", "apparentAngle", "trueAngle", "apparentSpeed", "trueSpeed"],
  number: ["speed"],
};
const angular = new Set(["heading", "apparentAngle", "trueAngle"]);
const modulo = (value, divisor) => ((value % divisor) + divisor) % divisor;
export function shortestAngleDelta(from, to) {
  return modulo(to - from + 180, 360) - 180;
}
export function interpolateInstrumentReading(from, to, fraction) {
  const t = Math.max(0, Math.min(1, fraction)),
    reading = { ...to };
  for (const key of Object.keys(to)) {
    if (
      typeof from[key] !== "number" ||
      typeof to[key] !== "number" ||
      key === "measuredAt"
    )
      continue;
    const value =
      from[key] +
      (angular.has(key)
        ? shortestAngleDelta(from[key], to[key])
        : to[key] - from[key]) *
        t;
    reading[key] =
      key === "heading"
        ? modulo(value, 360)
        : angular.has(key)
          ? modulo(value + 180, 360) - 180
          : value;
  }
  return reading;
}
function qualifiedReading(kind, reading, quantizeSpeed = false) {
  if (!reading || !channels[kind].every((key) => Number.isFinite(reading[key])))
    throw new TypeError(
      `A ${kind} preview requires finite ${channels[kind].join(", ")} readings.`,
    );
  if (channels[kind].some((key) => !angular.has(key) && reading[key] < 0))
    throw new RangeError("Instrument speeds must be nonnegative.");
  if (reading.measuredAt !== undefined && !Number.isFinite(reading.measuredAt))
    throw new TypeError("Measurement time must be finite.");
  const result = Object.fromEntries(
    [...channels[kind], "measuredAt"]
      .filter((key) => key in reading)
      .map((key) => [key, reading[key]]),
  );
  // Reels settle on the displayed tenth while retaining smooth fractional motion in between.
  if (quantizeSpeed) result.speed = Math.round(result.speed * 10) / 10;
  return result;
}
let sequence = 0;
/** Animated design studies. Real telemetry must be supplied by the authenticated host. */
export class LiveInstrumentPreview {
  constructor(
    container,
    { kind, variant, reading, history = [], duration = 1400 },
  ) {
    if (
      !Object.hasOwn(instrumentDesignNames, kind) ||
      !Object.hasOwn(instrumentDesignNames[kind], variant)
    )
      throw new TypeError("Unknown instrument design.");
    if (!Number.isFinite(duration))
      throw new TypeError("Animation duration must be finite.");
    this.kind = kind;
    this.variant = variant;
    this.duration = Math.max(0, Math.min(5000, duration));
    this.reducedMotion =
      globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ??
      false;
    this.paused = false;
    this.disposed = false;
    const [create, update] = designRenderer(kind, variant);
    this.update = update;
    container.innerHTML = create(variant, `live-instrument-${++sequence}`);
    this.svg = container.querySelector("svg");
    this.svg.setAttribute("role", "img");
    this.svg.dataset.design = `${kind}:${variant}`;
    this.current = qualifiedReading(kind, reading, variant === "odometer");
    this.history = history
      .slice(-48)
      .map((sample) => qualifiedReading(kind, sample));
    this.draw();
  }
  draw() {
    this.update(this.svg, this.variant, this.current, this.history);
    const value =
      this.kind === "wind"
        ? `True wind ${this.current.trueSpeed.toFixed(1)} knots. Apparent wind ${this.current.apparentSpeed.toFixed(1)} knots.`
        : `Speed over ground ${this.current.speed.toFixed(1)} knots.`;
    this.svg.setAttribute(
      "aria-label",
      `${instrumentDesignNames[this.kind][this.variant]}. ${value}`,
    );
    this.svg.dataset.reading = String(
      this.kind === "wind" ? this.current.trueSpeed : this.current.speed,
    );
  }
  setReading(reading, { history = this.history, animate = true } = {}) {
    if (this.disposed) return;
    this.target = qualifiedReading(
      this.kind,
      reading,
      this.variant === "odometer",
    );
    this.history = history
      .slice(-48)
      .map((sample) => qualifiedReading(this.kind, sample));
    cancelAnimationFrame(this.frame);
    this.from = { ...this.current };
    if (!animate || this.reducedMotion || !this.duration) {
      this.current = { ...this.target };
      this.draw();
    } else if (!this.paused) this.start();
  }
  start() {
    this.startedAt = performance.now();
    let previousFrame = 0;
    const tick = (now) => {
      if (this.disposed || this.paused) return;
      const progress = Math.min(1, (now - this.startedAt) / this.duration);
      if (now - previousFrame >= 1000 / 40 || progress === 1) {
        // Smooth acceleration and settling, with no overshoot of measured values.
        const eased = progress * progress * (3 - 2 * progress);
        this.current = interpolateInstrumentReading(
          this.from,
          this.target,
          eased,
        );
        this.draw();
        previousFrame = now;
      }
      if (progress < 1) this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }
  pause() {
    this.paused = true;
    cancelAnimationFrame(this.frame);
  }
  resume() {
    if (this.disposed) return;
    cancelAnimationFrame(this.frame);
    this.paused = false;
    if (this.target) {
      this.from = { ...this.current };
      if (this.reducedMotion || !this.duration) {
        this.current = { ...this.target };
        this.draw();
      } else this.start();
    }
  }
  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.frame);
  }
}
